import { useState, useEffect } from 'react';
import { useAlert } from '../functions/useAlert';
import { fetchMsStatus, getMsOAuthUrl, disconnectMs } from '../services/connectors.service';

interface MsStatus {
  isConnected: boolean;
  needsReconnect: boolean;
  m365NotConfigured: boolean;
  adminDisabled: boolean;
  email?: string;
  /**
   * Scopes this connection was never granted, e.g. ["Files.Read"]. A connection
   * can be perfectly healthy for what it was consented to and still be unable
   * to do something newer — the status probe only exercises contacts, so
   * without this the card would advertise capabilities that do not work.
   * Only a disconnect/reconnect can add a scope; a refresh never does.
   */
  missingScopes: string[];
  loading: boolean;
}

export type { MsStatus };

const DEFAULT_STATUS: MsStatus = {
  isConnected: false, needsReconnect: false,
  m365NotConfigured: false, adminDisabled: false, missingScopes: [], loading: true,
};

/**
 * Microsoft 365 connection state for the integrations page.
 *
 * Calendar-sync settings/stats/log/reconnect used to live here. They were removed
 * with the meeting-sync subsystem — meetings are now discovered per-client on the
 * client page (see useDiscoveredMeetings) rather than auto-imported in the
 * background, so there is nothing to configure.
 */
export const useMicrosoftIntegration = () => {
  const [setAlert] = useAlert();
  const [msStatus, setMsStatus] = useState<MsStatus>(DEFAULT_STATUS);
  const [msDisconnecting, setMsDisconnecting] = useState(false);

  const loadStatus = async () => {
    try {
      const res: any = await fetchMsStatus();
      setMsStatus({
        isConnected: res.isConnected === true,
        needsReconnect: res.needsReconnect === true,
        m365NotConfigured: res.m365NotConfigured === true,
        adminDisabled: res.adminDisabled === true,
        email: res.email,
        missingScopes: Array.isArray(res.missingScopes) ? res.missingScopes : [],
        loading: false,
      });
    } catch {
      setMsStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    loadStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('outlook_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      loadStatus();
      setAlert('Microsoft account connected successfully!', 'success');
    }
    if (params.get('outlook_error') === 'wrong_organisation') {
      window.history.replaceState({}, '', window.location.pathname);
      setAlert('This Microsoft account belongs to a different organisation. Please sign in with your company account.', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMsConnect = async () => {
    try {
      const res: any = await getMsOAuthUrl(window.location.href);
      window.location.href = res.url;
    } catch (err: any) {
      setAlert(err?.message || 'Failed to start Microsoft sign-in. Ensure Microsoft 365 is connected in Organisation Settings.', 'error');
    }
  };

  const handleMsDisconnect = async () => {
    setMsDisconnecting(true);
    try {
      await disconnectMs();
      setAlert('Microsoft account disconnected.', 'success');
      loadStatus();
    } catch (err: any) {
      setAlert(err?.message || 'Failed to disconnect Microsoft account.', 'error');
    } finally {
      setMsDisconnecting(false);
    }
  };

  return { msStatus, msDisconnecting, handleMsConnect, handleMsDisconnect };
};
